import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileSpreadsheet,
  FlaskConical,
  Check,
  Map,
  Loader2,
  ArrowRight,
  Zap,
  Droplets,
  Leaf,
  Recycle,
  Handshake,
} from "lucide-react";
import {
  searchOrganizations,
  submitSummaryMetrics,
  SummaryMetricsCreateRequest,
  getOrganizationById,
  updateOrganizationSector,
  submitSustainabilityRoadmap,
} from "./utils";

type DataSourceType = "accounting" | "manual" | "roadmap" | null;

interface FieldConfig {
  id: string;
  label: string;
  type: "text" | "number" | "select" | "textarea" | "date" | "email";
  placeholder: string;
  unit?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  fullWidth?: boolean;
  step?: string;
  showWhen?: { field: string; value: string };
  forcedValue?: { when: { field: string; value: string }; value: string };
}

type OrgOption = {
  organizationId: number;
  organizationName: string;
  partnerId?: number | null;
};

type RoadmapAreaConfig = {
  key: string;
  title: string;
  subtitle: string;
  currentLabel: string;
  secondaryPlaceholderLabel?: string;
  secondaryPlaceholderValue?: string;
  icon: React.ReactNode;
};
/* ---------------- Field configs ---------------- */

const commonPartnerField: FieldConfig = {
  id: "partner_name",
  label: "Partner Name",
  type: "text",
  placeholder: "Search organization (min 2 letters)",
  required: true,
};

const commonSubCategoryField: FieldConfig = {
  id: "sub_category",
  label: "Sub Category",
  type: "select",
  placeholder: "Select sub category",
  required: true,
  options: [
    { value: "organizational", label: "Organizational" },
    { value: "services", label: "Services" },
  ],
};

const commonTypeOfPartnerField: FieldConfig = {
  id: "type_of_partner",
  label: "Type of Partner",
  type: "select",
  placeholder: "Select partner type",
  required: true,
  options: [
    { value: "partner", label: "Partner" },
    { value: "impact_partner", label: "Impact Partner" },
  ],
};

const commonLandscapeField: FieldConfig = {
  id: "landscape",
  label: "Landscape",
  type: "select",
  placeholder: "Select landscape",
  required: true,
  options: [
    { value: "paucartambo", label: "Paucartambo Landscape" },
    { value: "abancay", label: "Abancay Landscape" },
    { value: "canete", label: "Cañete Landscape" },
    { value: "requena", label: "Requena Landscape" },
    { value: "machu_picchu", label: "Machu Picchu Landscape" },
    { value: "manu", label: "Manu Landscape" },
  ],
};
const SECTOR_OPTIONS: { value: string; label: string }[] = [
  // 1.x
  {
    value: "Agriculture & agro-industry",
    label: "1.1 Agriculture & agro-industry",
  },
  {
    value: "Fisheries and aquaculture",
    label: "1.2 Fisheries and aquaculture",
  },
  {
    value: "Mining & hydrocarbons",
    label: "1.3 Mining & hydrocarbons",
  },
  {
    value: "Forestry & Ecosystems Services",
    label: "1.4 Forestry & Ecosystems Services",
  },

  // 2.x
  {
    value: "Energy generation & public utilities",
    label: "2.3 Energy generation & public utilities",
  },
  {
    value: "Transport, automotive & aviation",
    label: "2.4 Transport, automotive & aviation",
  },
  {
    value: "Manufacturing & industry",
    label: "2.1 Manufacturing & industry",
  },
  {
    value: "Construction, Built Environment & real estate",
    label: "2.2 Construction, Built Environment & real estate",
  },

  // 3.x
  {
    value: "Food & drink",
    label: "3.1 Food & drink",
  },
  {
    value: "Education",
    label: "3.2 Education",
  },
  {
    value: "Health",
    label: "3.3 Health",
  },
  {
    value: "Security & technology",
    label: "3.4 Security & technology",
  },
  {
    value: "Telecommunications & media",
    label: "3.5 Telecommunications & media",
  },
  {
    value: "Commerce & retail",
    label: "3.6 Commerce & retail",
  },
  {
    value: "Tourism & hospitality",
    label: "3.7 Tourism & hospitality",
  },
  {
    value: "Events, music, culture & entertainment",
    label: "3.8 Events, music, culture & entertainment",
  },
  {
    value: "Financial & Legal Services",
    label: "3.9 Financial & Legal Services",
  },
  {
    value: "Technical & Other Services",
    label: "3.10 Technical & Other Services",
  },

  // 4.x
  {
    value: "Government services",
    label: "4.1 Government services",
  },
  {
    value: "Non-profit & Others",
    label: "4.2 Non-profit & Others",
  },
];

const commonSectorField: FieldConfig = {
  id: "sector",
  label: "Sector",
  type: "select",
  placeholder: "Select sector",
  required: true,
  options: SECTOR_OPTIONS,
};

const accountingFields: FieldConfig[] = [
  {
    id: "date",
    label: "Date",
    type: "date",
    placeholder: "YYYY-MM-DD",
    required: true,
  },
  commonPartnerField,
  commonSectorField,

  {
    id: "category",
    label: "Category",
    type: "select",
    placeholder: "Select category",
    required: true,
    options: [
      { value: "offsetting", label: "Offsetting" },
      { value: "emissions", label: "Emissions" },
    ],
  },
  commonSubCategoryField,
  {
    id: "invested_in_nature",
    label: "Invested in Nature",
    type: "number",
    placeholder: "0.00",
    step: "0.01",
    unit: "pts",
    forcedValue: {
      when: { field: "category", value: "emissions" },
      value: "0.00",
    },
  },
  {
    id: "total_emission_till_now",
    label: "Total Emission Till Now",
    type: "number",
    placeholder: "0.00",
    step: "0.01",
    forcedValue: {
      when: { field: "category", value: "offsetting" },
      value: "0",
    },
  },
  {
    id: "compensated",
    label: "Compensated",
    type: "number",
    placeholder: "0.00",
    step: "0.01",
    showWhen: { field: "category", value: "offsetting" },
  },
  {
    id: "plan",
    label: "Plan",
    type: "select",
    placeholder: "Select plan (optional)",
    options: [
      { value: "null", label: "None" },
      { value: "seed", label: "Seed" },
      { value: "tree", label: "Tree" },
      { value: "forest", label: "Forest" },
      { value: "landscape", label: "Landscape" },
    ],
  },
  commonTypeOfPartnerField,
  commonLandscapeField,
];

const manualFields: FieldConfig[] = [
  // Date (quarter/year auto)
  {
    id: "date",
    label: "Date",
    type: "date",
    placeholder: "YYYY-MM-DD",
    required: true,
  },

  // Year shown as INT (auto-filled from date, disabled)
  {
    id: "year_manual",
    label: "Year",
    type: "number",
    placeholder: "2025",
    required: true,
  },

  commonPartnerField,

  // Category is always emissions → no field
  commonSubCategoryField,
  commonSectorField,
  // Organizational: annual total emission (will be divided by 12 and inserted 12 months)
  {
    id: "annual_total_emission",
    label: "Total Emission (Annual)",
    type: "number",
    placeholder: "0.00",
    step: "0.01",
    showWhen: { field: "sub_category", value: "organizational" },
    required: true,
  },

  // Services: factor + packages; total emission auto computed
  {
    id: "factor",
    label: "Factor",
    type: "number",
    placeholder: "0.00",
    step: "0.0001",
    showWhen: { field: "sub_category", value: "services" },
    required: true,
  },
  {
    id: "total_packages_sold",
    label: "Total Packages Sold",
    type: "number",
    placeholder: "0",
    step: "1",
    showWhen: { field: "sub_category", value: "services" },
    required: true,
  },
  {
    id: "total_emission_computed",
    label: "Total Emission Till Now",
    type: "number",
    placeholder: "0.00",
    step: "0.01",
    showWhen: { field: "sub_category", value: "services" },
  },

  commonTypeOfPartnerField,
  commonLandscapeField,
];
const roadmapAreas: RoadmapAreaConfig[] = [
  {
    key: "energy_emissions",
    title: "Energy and emissions",
    subtitle: "Capture baseline, target and current emissions values",
    currentLabel: "Current emissions",
    icon: <Zap size={16} />,
  },
  {
    key: "water_management",
    title: "Water Management",
    subtitle: "Capture baseline, target and current water consumption values",
    currentLabel: "Current consumption",
    icon: <Droplets size={16} />,
  },
  {
    key: "nature_stewardship",
    title: "Nature Stewardship",
    subtitle: "Capture baseline, target and currently supported area",
    currentLabel: "Currently supporting",
    icon: <Leaf size={16} />,
  },
  {
    key: "waste_management",
    title: "Waste Management",
    subtitle: "Capture recovery rate and annual waste details",
    currentLabel: "Current",
    secondaryPlaceholderLabel: "Total waste per year",
    secondaryPlaceholderValue: "47000",
    icon: <Recycle size={16} />,
  },
  {
    key: "land_governance",
    title: "Land Governance",
    subtitle: "Capture partnerships and training activity",
    currentLabel: "Current",
    secondaryPlaceholderLabel: "Total trainings this year",
    secondaryPlaceholderValue: "3",
    icon: <Handshake size={16} />,
  },
];

const getRoadmapFieldKey = (
  areaKey: string,
  field:
    | "baseline_value"
    | "target_value"
    | "current_value"
    | "secondary_label"
    | "secondary_value",
) => `${areaKey}__${field}`;
/* ---------------- helpers ---------------- */

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const getLandscapeIdFromValue = (value: string): number | null => {
  const map: Record<string, number> = {
    manu: 1,
    machu_picchu: 2,
    requena: 3,
    canete: 4,
    abancay: 5,
    paucartambo: 6,
  };
  return map[value] ?? null;
};

const calcQuarterFromDate = (dateStr: string): number | null => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const m = d.getMonth(); // 0..11
  return Math.floor(m / 3) + 1; // 1..4
};

const addMonthsKeepDay = (dateStr: string, monthsToAdd: number): string => {
  const d = new Date(dateStr);
  const day = d.getDate();
  const target = new Date(d);
  target.setMonth(target.getMonth() + monthsToAdd);

  if (target.getDate() !== day) {
    target.setDate(0);
  }
  return target.toISOString().slice(0, 10);
};

/* ---------------- component ---------------- */

export const DataEntryForm: React.FC = () => {
  const [dataSource, setDataSource] = useState<DataSourceType>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Partner search (Accounting + Manual)
  const [orgQuery, setOrgQuery] = useState("");
  const [orgOptions, setOrgOptions] = useState<OrgOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgOption | null>(null);
  const [isOrgLoading, setIsOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const [isSectorLocked, setIsSectorLocked] = useState(false);

  const [roadmapFile, setRoadmapFile] = useState<File | null>(null);
  const [roadmapDocumentName, setRoadmapDocumentName] = useState("");

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const fields = useMemo(() => {
    if (dataSource === "accounting") return accountingFields;
    if (dataSource === "manual") return manualFields;
    if (dataSource === "roadmap") return [];
    return [];
  }, [dataSource]);

  const handleInputChange = (id: string, value: string) => {
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // close org dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target as Node)) {
        setOrgOptions([]);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isFieldVisible = (field: FieldConfig): boolean => {
    if (!field.showWhen) return true;
    return formData[field.showWhen.field] === field.showWhen.value;
  };

  const getFieldState = (
    field: FieldConfig,
  ): { disabled: boolean; value: string } => {
    if (field.id === "sector") {
      return { disabled: isSectorLocked, value: formData.sector || "" };
    }
    // manual: year_manual disabled (auto from date)
    if (dataSource === "manual" && field.id === "year_manual") {
      return { disabled: true, value: formData.year_manual || "" };
    }

    // manual: computed emission disabled
    if (dataSource === "manual" && field.id === "total_emission_computed") {
      return { disabled: true, value: formData.total_emission_computed || "0" };
    }

    if (field.forcedValue) {
      const conditionMet =
        formData[field.forcedValue.when.field] === field.forcedValue.when.value;
      if (conditionMet) {
        return { disabled: true, value: field.forcedValue.value };
      }
    }
    return { disabled: false, value: formData[field.id] || "" };
  };

  const visibleFields = fields.filter(isFieldVisible);

  // Reset when dataSource changes
  useEffect(() => {
    setSubmitError(null);
    setOrgError(null);
    setOrgOptions([]);
    setOrgQuery("");
    setSelectedOrg(null);
    setFormData({});
    setIsSectorLocked(false);
    setRoadmapFile(null);
    setRoadmapDocumentName("");
  }, [dataSource]);

  // Partner org search (debounced) — for BOTH accounting/manual
  useEffect(() => {
    if (!dataSource) return;

    const q = orgQuery.trim();
    if (selectedOrg && q === selectedOrg.organizationName) {
      setOrgOptions([]);
      setOrgError(null);
      return;
    }

    if (q.length < 2) {
      setOrgOptions([]);
      setOrgError(null);
      return;
    }

    const t = setTimeout(async () => {
      setIsOrgLoading(true);
      setOrgError(null);

      const resp = await searchOrganizations(q, 5);

      if (resp.status === "error") {
        setOrgOptions([]);
        setOrgError(resp.message || "Failed to fetch organizations");
      } else {
        const list = resp.data || [];
        const filtered = selectedOrg
          ? list.filter((o) => o.organizationId !== selectedOrg.organizationId)
          : list;
        setOrgOptions(filtered);
      }

      setIsOrgLoading(false);
    }, 300);

    return () => clearTimeout(t);
  }, [orgQuery, dataSource, selectedOrg]);

  const selectOrganization = async (opt: OrgOption) => {
    setSelectedOrg(opt);
    setOrgQuery(opt.organizationName);
    setOrgOptions([]);
    handleInputChange("partner_name", opt.organizationName);

    try {
      const orgResp = await getOrganizationById(opt.organizationId);
      if (orgResp.status === "error") throw new Error(orgResp.message);

      // ✅ sector might be at orgResp.data.sector OR orgResp.data.data.sector
      const rawSector =
        (orgResp as any)?.data?.sector ??
        (orgResp as any)?.data?.data?.sector ??
        "";

      const sectorFromDb = String(rawSector ?? "").trim();

      if (sectorFromDb) {
        setFormData((prev) => ({ ...prev, sector: sectorFromDb }));
        setIsSectorLocked(true); // ✅ will render input instead of select
      } else {
        setFormData((prev) => ({ ...prev, sector: "" }));
        setIsSectorLocked(false);
      }
    } catch (e) {
      setFormData((prev) => ({ ...prev, sector: "" }));
      setIsSectorLocked(false);
    }
  };

  // clear selection if user edits query after selection
  useEffect(() => {
    if (!dataSource) return;
    if (!selectedOrg) return;
    if (orgQuery.trim() !== selectedOrg.organizationName) {
      setSelectedOrg(null);
      setIsSectorLocked(false);
      setFormData((prev) => ({ ...prev, sector: "" }));
    }
  }, [orgQuery]);

  // Accounting: keep old forced logic
  useEffect(() => {
    if (dataSource !== "accounting") return;

    if (formData.category === "offsetting") {
      setFormData((prev) => ({
        ...prev,
        total_emission_till_now: "0",
      }));
    }

    if (formData.category === "emissions") {
      setFormData((prev) => ({
        ...prev,
        invested_in_nature: "0.00",
        compensated: "0",
      }));
    }
  }, [formData.category, dataSource]);

  // Manual: date -> year_manual auto
  useEffect(() => {
    if (dataSource !== "manual") return;
    const dateStr = formData.date;
    if (!dateStr) {
      setFormData((prev) => ({ ...prev, year_manual: "" }));
      return;
    }
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return;
    setFormData((prev) => ({ ...prev, year_manual: String(d.getFullYear()) }));
  }, [formData.date, dataSource]);

  // Manual Services: compute total emission = factor * packages
  useEffect(() => {
    if (dataSource !== "manual") return;
    if (formData.sub_category !== "services") return;

    const factor = Number(formData.factor || 0);
    const packages = Number(formData.total_packages_sold || 0);
    const total = round2(factor * packages);

    setFormData((prev) => ({
      ...prev,
      total_emission_computed: String(total),
    }));
  }, [
    formData.factor,
    formData.total_packages_sold,
    formData.sub_category,
    dataSource,
  ]);

  const validateBeforeSubmit = (): string | null => {
    if (!dataSource) return "Please select a data source.";

    if (!selectedOrg?.organizationId) {
      return "Please select a Partner (organization).";
    }

    if (dataSource === "accounting") {
      if (!formData.type_of_partner) return "Type of Partner is required.";
      if (!formData.landscape) return "Landscape is required.";
      if (!formData.sector) return "Sector is required.";

      if (!formData.date) return "Date is required.";
      if (!formData.category) return "Category is required.";
      if (!formData.sub_category) return "Sub Category is required.";

      if (formData.invested_in_nature === undefined)
        return "Invested in Nature is required.";

      const totalEmission =
        formData.category === "offsetting"
          ? "0"
          : (formData.total_emission_till_now ?? "").trim();

      if (totalEmission === "") return "Total Emission Till Now is required.";

      if (formData.category === "offsetting" && !formData.compensated) {
        return "Compensated is required for Offsetting.";
      }

      return null;
    }

    if (dataSource === "manual") {
      if (!formData.type_of_partner) return "Type of Partner is required.";
      if (!formData.landscape) return "Landscape is required.";
      if (!formData.sector) return "Sector is required.";

      if (!formData.date) return "Date is required.";
      if (!formData.sub_category) return "Sub Category is required.";

      if (formData.sub_category === "organizational") {
        if (!formData.annual_total_emission)
          return "Total Emission (Annual) is required for Organizational.";
      }

      if (formData.sub_category === "services") {
        if (!formData.factor) return "Factor is required for Services.";
        if (!formData.total_packages_sold)
          return "Total Packages Sold is required for Services.";
      }

      return null;
    }

    if (dataSource === "roadmap") {
      if (!selectedOrg?.partnerId) {
        return "Selected partner does not have a valid account id.";
      }

      if (!formData.sector) return "Sector is required.";

      if (!formData.baseline_year) return "Baseline Year is required.";
      if (!formData.target_year) return "Target Year is required.";

      const baselineYear = Number(formData.baseline_year);
      const targetYear = Number(formData.target_year);

      if (targetYear <= baselineYear) {
        return "Target Year must be greater than Baseline Year.";
      }
      if (!roadmapFile) {
        return "Roadmap document is required.";
      }
      for (const area of roadmapAreas) {
        const baseline =
          formData[getRoadmapFieldKey(area.key, "baseline_value")];
        const target = formData[getRoadmapFieldKey(area.key, "target_value")];
        const current = formData[getRoadmapFieldKey(area.key, "current_value")];

        if (baseline === undefined || baseline === "") {
          return `${area.title}: baseline value is required.`;
        }
        if (target === undefined || target === "") {
          return `${area.title}: target value is required.`;
        }
        if (current === undefined || current === "") {
          return `${area.title}: current value is required.`;
        }

        if (area.key === "waste_management" || area.key === "land_governance") {
          const secondaryValue =
            formData[getRoadmapFieldKey(area.key, "secondary_value")];

          if (secondaryValue === undefined || secondaryValue === "") {
            return `${area.title}: secondary value is required.`;
          }
        }
      }

      return null;
    }

    return null;
  };
  const buildRoadmapPayload = () => {
    return {
      account_id: selectedOrg?.partnerId ?? 0,
      baseline_year: Number(formData.baseline_year || 0),
      target_year: Number(formData.target_year || 0),
      priority_areas: roadmapAreas.map((area, index) => ({
        priority_key: area.key,
        sort_order: index + 1,
        baseline_value: Number(
          formData[getRoadmapFieldKey(area.key, "baseline_value")] || 0,
        ),
        target_value: Number(
          formData[getRoadmapFieldKey(area.key, "target_value")] || 0,
        ),
        current_value: Number(
          formData[getRoadmapFieldKey(area.key, "current_value")] || 0,
        ),
        secondary_label:
          area.key === "waste_management"
            ? "Total waste per year"
            : area.key === "land_governance"
            ? "Total trainings this year"
            : formData[getRoadmapFieldKey(area.key, "secondary_label")] || null,
        secondary_value:
          formData[getRoadmapFieldKey(area.key, "secondary_value")] !== "" &&
          formData[getRoadmapFieldKey(area.key, "secondary_value")] !==
            undefined
            ? Number(formData[getRoadmapFieldKey(area.key, "secondary_value")])
            : null,
      })),
    };
  };

  const handleReset = () => {
    setDataSource(null);
    setFormData({});
    setIsSubmitted(false);
    setOrgQuery("");
    setOrgOptions([]);
    setSelectedOrg(null);
    setSubmitError(null);
    setOrgError(null);
  };

  if (isSubmitted) {
    return (
      <CenterWrap>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{ textAlign: "center", maxWidth: 420 }}
        >
          <SuccessIconWrap>
            <Check size={28} color="#fff" />
          </SuccessIconWrap>

          <H2>Data Submitted Successfully</H2>
          <P>
            Your
            {dataSource === "accounting"
              ? "accounting records"
              : dataSource === "manual"
              ? "manual study"
              : "sustainability roadmap"}
            data has been received.
          </P>

          <PrimaryButton type="button" onClick={handleReset}>
            Submit Another Entry
          </PrimaryButton>
        </motion.div>
      </CenterWrap>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const validationErr = validateBeforeSubmit();
    if (validationErr) {
      setSubmitError(validationErr);
      return;
    }

    setIsSubmitting(true);

    try {
      if (!isSectorLocked) {
        const chosenSector = (formData.sector || "").trim();
        if (chosenSector) {
          const up = await updateOrganizationSector(
            selectedOrg!.organizationId,
            chosenSector,
          );
          if (up.status === "error") throw new Error(up.message);

          setIsSectorLocked(true);
        }
      }
      // ---------- ACCOUNTING ----------
      if (dataSource === "accounting") {
        const landscape_id = getLandscapeIdFromValue(formData.landscape);
        if (!landscape_id) throw new Error("Invalid landscape selection.");

        const dateStr = formData.date;
        const q = calcQuarterFromDate(dateStr);
        if (!q) throw new Error("Invalid date.");

        const y = new Date(dateStr).getFullYear();

        const payload: SummaryMetricsCreateRequest = {
          date: dateStr,
          sourceType: "accounting",
          quarter: q,
          year: y,
          organizationId: selectedOrg!.organizationId,
          partnerName: selectedOrg!.organizationName,
          category: formData.category as "offsetting" | "emissions",
          sub_category: formData.sub_category,
          invested_in_nature: Number(formData.invested_in_nature || 0),
          total_emmission_till_now:
            formData.category === "offsetting"
              ? 0
              : Number(formData.total_emission_till_now || 0),
          compensated:
            formData.category === "offsetting"
              ? -Math.abs(Number(formData.compensated || 0))
              : 0,

          plan:
            formData.plan && formData.plan !== "null"
              ? (formData.plan as any)
              : "null",

          type_of_partner: formData.type_of_partner as
            | "partner"
            | "impact_partner",
          landscape_id,
        };

        const resp = await submitSummaryMetrics(payload);
        if (resp.status === "error") throw new Error(resp.message);

        setIsSubmitted(true);
        return;
      }

      // ---------- MANUAL STUDIES ----------
      if (dataSource === "manual") {
        const landscape_id = getLandscapeIdFromValue(formData.landscape);
        if (!landscape_id) throw new Error("Invalid landscape selection.");

        const dateStr = formData.date;
        const q = calcQuarterFromDate(dateStr);
        if (!q) throw new Error("Invalid date.");

        const baseYear = Number(
          formData.year_manual || new Date(dateStr).getFullYear(),
        );

        // category always emissions
        const category = "emissions" as const;
        const invested_in_nature = 0;
        const compensated = 0;
        const plan = "null" as const;

        // 1) Organizational => annual/12, 12 monthly inserts
        if (formData.sub_category === "organizational") {
          const annual = Number(formData.annual_total_emission || 0);
          const perMonth = round2(annual / 12);

          // create 12 rows: from selected date month for next 12 months
          const requests: SummaryMetricsCreateRequest[] = Array.from({
            length: 12,
          }).map((_, idx) => {
            const monthDate = addMonthsKeepDay(dateStr, idx);
            const q2 = calcQuarterFromDate(monthDate) || 1;
            const y2 = new Date(monthDate).getFullYear();

            return {
              date: monthDate,
              sourceType: "manual",
              quarter: q2,
              year: y2,

              organizationId: selectedOrg!.organizationId,
              partnerName: selectedOrg!.organizationName,

              category,
              sub_category: "organizational",

              invested_in_nature,
              total_emmission_till_now: perMonth,
              compensated,

              plan,
              type_of_partner: formData.type_of_partner as
                | "partner"
                | "impact_partner",
              landscape_id,
            };
          });

          // send sequentially to avoid overload + easier debug
          for (const req of requests) {
            const resp = await submitSummaryMetrics(req);
            if (resp.status === "error") {
              throw new Error(
                `Manual Organizational insert failed for date ${req.date}: ${resp.message}`,
              );
            }
          }

          setIsSubmitted(true);
          return;
        }

        // 2) Services => factor * packages => single row
        if (formData.sub_category === "services") {
          const factor = Number(formData.factor || 0);
          const packages = Number(formData.total_packages_sold || 0);
          const total = round2(factor * packages);

          const payload: SummaryMetricsCreateRequest = {
            date: dateStr,

            sourceType: "manual",
            quarter: q,
            year: baseYear,

            organizationId: selectedOrg!.organizationId,
            partnerName: selectedOrg!.organizationName,

            category,
            sub_category: "services",

            invested_in_nature,
            total_emmission_till_now: total,
            compensated,

            plan,
            type_of_partner: formData.type_of_partner as
              | "partner"
              | "impact_partner",
            landscape_id,
          };

          const resp = await submitSummaryMetrics(payload);
          if (resp.status === "error") throw new Error(resp.message);

          setIsSubmitted(true);
          return;
        }

        throw new Error("Invalid sub category.");
      }
      // if (dataSource === "roadmap") {
      //   const payload = buildRoadmapPayload();

      //   const resp = await submitSustainabilityRoadmap(payload);
      //   if (resp.status === "error") throw new Error(resp.message);

      //   setIsSubmitted(true);
      //   return;
      // }
      if (dataSource === "roadmap") {
        const accountId = selectedOrg?.partnerId;
        if (!accountId) {
          throw new Error("Selected partner does not have a valid account id.");
        }

        const payload = {
          ...buildRoadmapPayload(),
          roadmap_document_url: null,
        };

        const resp = await submitSustainabilityRoadmap(payload, roadmapFile);
        if (resp.status === "error") throw new Error(resp.message);

        setIsSubmitted(true);
        return;
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page>
      <Main>
        <TitleWrap>
          <H1>Environmental Data Entry</H1>
          <SubText>
            Select your data source and fill in the details below
          </SubText>
        </TitleWrap>

        <SourceGrid>
          <SourceCard
            type="button"
            $active={dataSource === "accounting"}
            onClick={() => setDataSource("accounting")}
          >
            <SourceRow>
              <IconBox $active={dataSource === "accounting"}>
                <FileSpreadsheet size={18} />
              </IconBox>
              <div>
                <SourceTitle>Accounting Records</SourceTitle>
                <SourceDesc>Financial data & invoices</SourceDesc>
              </div>
              {dataSource === "accounting" && (
                <Check
                  size={18}
                  color="var(--action-green)"
                  style={{ marginLeft: "auto" }}
                />
              )}
            </SourceRow>
          </SourceCard>

          <SourceCard
            type="button"
            $active={dataSource === "manual"}
            onClick={() => setDataSource("manual")}
          >
            <SourceRow>
              <IconBox $active={dataSource === "manual"}>
                <FlaskConical size={18} />
              </IconBox>
              <div>
                <SourceTitle>Footprint Studies</SourceTitle>
                <SourceDesc>Direct measurements</SourceDesc>
              </div>
              {dataSource === "manual" && (
                <Check
                  size={18}
                  color="var(--action-green)"
                  style={{ marginLeft: "auto" }}
                />
              )}
            </SourceRow>
          </SourceCard>
          <SourceCard
            type="button"
            $active={dataSource === "roadmap"}
            onClick={() => setDataSource("roadmap")}
          >
            <SourceRow>
              <IconBox $active={dataSource === "roadmap"}>
                <Map size={18} />
              </IconBox>
              <div>
                <SourceTitle>Sustainability Roadmap Studies</SourceTitle>
                <SourceDesc>Partner roadmap metrics & targets</SourceDesc>
              </div>
              {dataSource === "roadmap" && (
                <Check
                  size={18}
                  color="var(--action-green)"
                  style={{ marginLeft: "auto" }}
                />
              )}
            </SourceRow>
          </SourceCard>
        </SourceGrid>

        {submitError && <ErrorBanner>{submitError}</ErrorBanner>}

        <AnimatePresence mode="wait">
          {dataSource &&
            (dataSource === "roadmap" ? (
              <motion.form
                key="roadmap"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.22 }}
                onSubmit={handleSubmit}
                style={{ maxWidth: 1180, margin: "0 auto" }}
              >
                <RoadmapTopGrid>
                  <RoadmapMetaCard>
                    <SectionMiniTitle>Partner details</SectionMiniTitle>

                    <FieldLabel htmlFor="roadmap_partner_name">
                      Partner Name<Req>*</Req>
                    </FieldLabel>

                    <OrgDropdown ref={dropdownRef}>
                      <OrgInput
                        id="roadmap_partner_name"
                        type="text"
                        placeholder="Search organization (min 2 letters)"
                        value={orgQuery}
                        onChange={(e) => {
                          setOrgQuery(e.target.value);
                          handleInputChange("partner_name", e.target.value);
                        }}
                        autoComplete="off"
                      />

                      {isOrgLoading && (
                        <OrgHintRow>
                          <SmallSpinner />
                          <OrgHintText>Searching...</OrgHintText>
                        </OrgHintRow>
                      )}

                      {!!orgError && (
                        <OrgHintRow>
                          <OrgHintText style={{ color: "#b91c1c" }}>
                            {orgError}
                          </OrgHintText>
                        </OrgHintRow>
                      )}

                      {!isOrgLoading &&
                        !orgError &&
                        orgQuery.trim().length >= 2 &&
                        orgOptions.length > 0 && (
                          <OrgMenu>
                            {orgOptions.map((opt) => (
                              <OrgItem
                                key={opt.organizationId}
                                type="button"
                                onClick={() => selectOrganization(opt)}
                              >
                                <OrgName>{opt.organizationName}</OrgName>
                              </OrgItem>
                            ))}
                          </OrgMenu>
                        )}

                      {!isOrgLoading &&
                        !orgError &&
                        orgQuery.trim().length >= 2 &&
                        orgOptions.length === 0 &&
                        !selectedOrg && (
                          <OrgHintRow>
                            <OrgHintText>No results</OrgHintText>
                          </OrgHintRow>
                        )}

                      {selectedOrg && (
                        <SelectedTag>
                          Selected: {selectedOrg.organizationName}
                        </SelectedTag>
                      )}
                    </OrgDropdown>

                    {formData.sector && (
                      <StaticInfoRow>
                        <StaticInfoLabel>Sector</StaticInfoLabel>
                        <StaticInfoValue>{formData.sector}</StaticInfoValue>
                      </StaticInfoRow>
                    )}
                  </RoadmapMetaCard>

                  <RoadmapMetaCard>
                    <SectionMiniTitle>Roadmap years</SectionMiniTitle>

                    <GridTwo>
                      <div>
                        <FieldLabel htmlFor="baseline_year">
                          Baseline Year<Req>*</Req>
                        </FieldLabel>
                        <InputWrap>
                          <InputEl
                            id="baseline_year"
                            type="number"
                            placeholder="2024"
                            value={formData.baseline_year || ""}
                            onChange={(e) =>
                              handleInputChange("baseline_year", e.target.value)
                            }
                          />
                        </InputWrap>
                      </div>

                      <div>
                        <FieldLabel htmlFor="target_year">
                          Target Year<Req>*</Req>
                        </FieldLabel>
                        <InputWrap>
                          <InputEl
                            id="target_year"
                            type="number"
                            placeholder="2029"
                            value={formData.target_year || ""}
                            onChange={(e) =>
                              handleInputChange("target_year", e.target.value)
                            }
                          />
                        </InputWrap>
                      </div>
                    </GridTwo>

                    <RoadmapHint>
                      These years will be used across all cards for baseline and
                      target labels.
                    </RoadmapHint>
                  </RoadmapMetaCard>
                </RoadmapTopGrid>

                <RoadmapSectionGrid>
                  {roadmapAreas.map((area) => {
                    const baselineYearLabel =
                      formData.baseline_year || "Baseline";
                    const targetYearLabel = formData.target_year || "Target";

                    return (
                      <RoadmapSectionCard key={area.key}>
                        <RoadmapSectionHeader>
                          <RoadmapSectionIcon>{area.icon}</RoadmapSectionIcon>
                          <div>
                            <RoadmapSectionTitle>
                              {area.title}
                            </RoadmapSectionTitle>
                            <RoadmapSectionSubtitle>
                              {area.subtitle}
                            </RoadmapSectionSubtitle>
                          </div>
                        </RoadmapSectionHeader>

                        <SectionFormGrid>
                          <div>
                            <FieldLabel
                              htmlFor={getRoadmapFieldKey(
                                area.key,
                                "baseline_value",
                              )}
                            >
                              {baselineYearLabel} baseline<Req>*</Req>
                            </FieldLabel>
                            <InputWrap>
                              <InputEl
                                id={getRoadmapFieldKey(
                                  area.key,
                                  "baseline_value",
                                )}
                                type="number"
                                placeholder="Enter baseline value"
                                value={
                                  formData[
                                    getRoadmapFieldKey(
                                      area.key,
                                      "baseline_value",
                                    )
                                  ] || ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    getRoadmapFieldKey(
                                      area.key,
                                      "baseline_value",
                                    ),
                                    e.target.value,
                                  )
                                }
                              />
                            </InputWrap>
                          </div>

                          <div>
                            <FieldLabel
                              htmlFor={getRoadmapFieldKey(
                                area.key,
                                "target_value",
                              )}
                            >
                              {targetYearLabel} target<Req>*</Req>
                            </FieldLabel>
                            <InputWrap>
                              <InputEl
                                id={getRoadmapFieldKey(
                                  area.key,
                                  "target_value",
                                )}
                                type="number"
                                placeholder="Enter target value"
                                value={
                                  formData[
                                    getRoadmapFieldKey(area.key, "target_value")
                                  ] || ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    getRoadmapFieldKey(
                                      area.key,
                                      "target_value",
                                    ),
                                    e.target.value,
                                  )
                                }
                              />
                            </InputWrap>
                          </div>

                          <div style={{ gridColumn: "1 / -1" }}>
                            <FieldLabel
                              htmlFor={getRoadmapFieldKey(
                                area.key,
                                "current_value",
                              )}
                            >
                              {area.currentLabel}
                              <Req>*</Req>
                            </FieldLabel>
                            <InputWrap>
                              <InputEl
                                id={getRoadmapFieldKey(
                                  area.key,
                                  "current_value",
                                )}
                                type="number"
                                placeholder="Enter current value"
                                value={
                                  formData[
                                    getRoadmapFieldKey(
                                      area.key,
                                      "current_value",
                                    )
                                  ] || ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    getRoadmapFieldKey(
                                      area.key,
                                      "current_value",
                                    ),
                                    e.target.value,
                                  )
                                }
                              />
                            </InputWrap>
                          </div>

                          {(area.key === "waste_management" ||
                            area.key === "land_governance") && (
                            <>
                              <div>
                                <FieldLabel
                                  htmlFor={getRoadmapFieldKey(
                                    area.key,
                                    "secondary_label",
                                  )}
                                >
                                  Secondary label<Req>*</Req>
                                </FieldLabel>
                                <InputWrap>
                                  <InputEl
                                    id={getRoadmapFieldKey(
                                      area.key,
                                      "secondary_label",
                                    )}
                                    type="text"
                                    value={
                                      area.key === "waste_management"
                                        ? "Total waste per year"
                                        : area.key === "land_governance"
                                        ? "Total trainings this year"
                                        : formData[
                                            getRoadmapFieldKey(
                                              area.key,
                                              "secondary_label",
                                            )
                                          ] || ""
                                    }
                                    disabled={
                                      area.key === "waste_management" ||
                                      area.key === "land_governance"
                                    }
                                    onChange={(e) =>
                                      handleInputChange(
                                        getRoadmapFieldKey(
                                          area.key,
                                          "secondary_label",
                                        ),
                                        e.target.value,
                                      )
                                    }
                                  />
                                </InputWrap>
                              </div>

                              <div>
                                <FieldLabel
                                  htmlFor={getRoadmapFieldKey(
                                    area.key,
                                    "secondary_value",
                                  )}
                                >
                                  Secondary value<Req>*</Req>
                                </FieldLabel>
                                <InputWrap>
                                  <InputEl
                                    id={getRoadmapFieldKey(
                                      area.key,
                                      "secondary_value",
                                    )}
                                    type="number"
                                    placeholder={
                                      area.secondaryPlaceholderValue ||
                                      "Enter value"
                                    }
                                    value={
                                      formData[
                                        getRoadmapFieldKey(
                                          area.key,
                                          "secondary_value",
                                        )
                                      ] || ""
                                    }
                                    onChange={(e) =>
                                      handleInputChange(
                                        getRoadmapFieldKey(
                                          area.key,
                                          "secondary_value",
                                        ),
                                        e.target.value,
                                      )
                                    }
                                  />
                                </InputWrap>
                              </div>
                            </>
                          )}
                        </SectionFormGrid>
                      </RoadmapSectionCard>
                    );
                  })}
                  <RoadmapSectionCard>
                    <RoadmapSectionHeader>
                      <RoadmapSectionIcon>
                        <Map size={16} />
                      </RoadmapSectionIcon>
                      <div>
                        <RoadmapSectionTitle>
                          Roadmap document
                        </RoadmapSectionTitle>
                        <RoadmapSectionSubtitle>
                          Upload the roadmap file for this selected partner. It
                          will be uploaded only when you click Save Roadmap.
                        </RoadmapSectionSubtitle>
                      </div>
                    </RoadmapSectionHeader>

                    <SectionFormGrid>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <FieldLabel htmlFor="roadmap_file">
                          Upload document<Req>*</Req>
                        </FieldLabel>
                        <InputWrap>
                          <InputEl
                            id="roadmap_file"
                            type="file"
                            accept=".pdf,.doc,.docx,.ppt,.pptx"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              setRoadmapFile(file);
                              setRoadmapDocumentName(file?.name || "");
                            }}
                          />
                        </InputWrap>
                      </div>

                      {roadmapDocumentName && (
                        <div style={{ gridColumn: "1 / -1" }}>
                          <StaticInfoRow>
                            <StaticInfoLabel>Selected file</StaticInfoLabel>
                            <StaticInfoValue>
                              {roadmapDocumentName}
                            </StaticInfoValue>
                          </StaticInfoRow>
                        </div>
                      )}
                    </SectionFormGrid>
                  </RoadmapSectionCard>
                </RoadmapSectionGrid>

                <Actions>
                  <PrimaryButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2
                          size={16}
                          className="spin"
                          style={{ marginRight: 8 }}
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        Save Roadmap
                        <ArrowRight size={16} style={{ marginLeft: 8 }} />
                      </>
                    )}
                  </PrimaryButton>
                </Actions>
              </motion.form>
            ) : (
              <motion.form
                key={dataSource}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.22 }}
                onSubmit={handleSubmit}
              >
                <Grid>
                  <AnimatePresence mode="popLayout">
                    {visibleFields.map((field, index) => {
                      const { disabled, value } = getFieldState(field);

                      // Custom partner dropdown (for BOTH sources)
                      if (field.id === "partner_name") {
                        return (
                          <motion.div
                            key={field.id}
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={{ duration: 0.18, delay: index * 0.01 }}
                            layout
                          >
                            <FieldLabel htmlFor={field.id}>
                              {field.label}
                              {field.required && <Req>*</Req>}
                            </FieldLabel>

                            <OrgDropdown ref={dropdownRef}>
                              <OrgInput
                                id={field.id}
                                type="text"
                                placeholder={field.placeholder}
                                value={orgQuery}
                                onChange={(e) => {
                                  setOrgQuery(e.target.value);
                                  handleInputChange(field.id, e.target.value);
                                }}
                                autoComplete="off"
                              />

                              {isOrgLoading && (
                                <OrgHintRow>
                                  <SmallSpinner />
                                  <OrgHintText>Searching...</OrgHintText>
                                </OrgHintRow>
                              )}

                              {!!orgError && (
                                <OrgHintRow>
                                  <OrgHintText style={{ color: "#b91c1c" }}>
                                    {orgError}
                                  </OrgHintText>
                                </OrgHintRow>
                              )}

                              {!isOrgLoading &&
                                !orgError &&
                                orgQuery.trim().length >= 2 &&
                                orgOptions.length > 0 && (
                                  <OrgMenu>
                                    {orgOptions.map((opt) => (
                                      <OrgItem
                                        key={opt.organizationId}
                                        type="button"
                                        onClick={() => selectOrganization(opt)}
                                      >
                                        <OrgName>
                                          {opt.organizationName}
                                        </OrgName>
                                      </OrgItem>
                                    ))}
                                  </OrgMenu>
                                )}

                              {!isOrgLoading &&
                                !orgError &&
                                orgQuery.trim().length >= 2 &&
                                orgOptions.length === 0 &&
                                !selectedOrg && (
                                  <OrgHintRow>
                                    <OrgHintText>No results</OrgHintText>
                                  </OrgHintRow>
                                )}

                              {selectedOrg && (
                                <SelectedTag>
                                  Selected: {selectedOrg.organizationName}
                                </SelectedTag>
                              )}
                            </OrgDropdown>
                          </motion.div>
                        );
                      }

                      return (
                        <motion.div
                          key={field.id}
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.98 }}
                          transition={{ duration: 0.18, delay: index * 0.01 }}
                          layout
                          style={{
                            gridColumn: field.fullWidth ? "1 / -1" : undefined,
                          }}
                        >
                          <FieldLabel htmlFor={field.id}>
                            {field.label}
                            {field.required && <Req>*</Req>}
                          </FieldLabel>

                          {field.id === "sector" && isSectorLocked ? (
                            <InputWrap>
                              <InputEl
                                id={field.id}
                                type="text"
                                value={value}
                                disabled={true}
                              />
                            </InputWrap>
                          ) : field.type === "select" ? (
                            <SelectWrap>
                              <SelectEl
                                id={field.id}
                                value={value}
                                onChange={(e) =>
                                  handleInputChange(field.id, e.target.value)
                                }
                                disabled={disabled}
                              >
                                <option value="" disabled>
                                  {field.placeholder}
                                </option>
                                {field.options?.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </SelectEl>
                            </SelectWrap>
                          ) : field.type === "textarea" ? (
                            <TextareaEl
                              id={field.id}
                              placeholder={field.placeholder}
                              value={value}
                              onChange={(e) =>
                                handleInputChange(field.id, e.target.value)
                              }
                              disabled={disabled}
                            />
                          ) : (
                            <InputWrap>
                              <InputEl
                                id={field.id}
                                type={field.type}
                                placeholder={field.placeholder}
                                value={value}
                                onChange={(e) =>
                                  handleInputChange(field.id, e.target.value)
                                }
                                disabled={disabled}
                                step={field.step}
                              />
                              {field.unit && <Unit>{field.unit}</Unit>}
                            </InputWrap>
                          )}
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </Grid>

                <Actions>
                  <PrimaryButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2
                          size={16}
                          className="spin"
                          style={{ marginRight: 8 }}
                        />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Data
                        <ArrowRight size={16} style={{ marginLeft: 8 }} />
                      </>
                    )}
                  </PrimaryButton>
                </Actions>
              </motion.form>
            ))}
        </AnimatePresence>

        {!dataSource && (
          <EmptyState>
            <p>Select a data source above to begin</p>
          </EmptyState>
        )}
      </Main>
    </Page>
  );
};

/* ---------------- styled-components ---------------- */

const Page = styled.div`
  min-height: 100vh;
  background: var(--background);
`;

const Main = styled.main`
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 28px 32px 60px;

  @media (max-width: 640px) {
    padding: 20px 16px 50px;
  }
`;

const TitleWrap = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const H1 = styled.h1`
  margin: 0 0 6px;
  font-size: 30px;
  color: var(--trust-green);
`;

const SubText = styled.p`
  margin: 0;
  color: rgba(0, 77, 55, 0.55);
`;

const SourceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  max-width: 1100px; /* slightly wider so 3 fit nicely */
  margin: 0 auto 26px;

  /* Tablet: 2 in a row */
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }

  /* Desktop: 3 in a row */
  @media (min-width: 980px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const SourceCard = styled.button<{ $active?: boolean }>`
  padding: 12px;
  border-radius: 14px;
  border: 2px solid
    ${(p) => (p.$active ? "var(--action-green)" : "var(--border)")};
  background: ${(p) =>
    p.$active ? "rgba(222, 245, 186, 0.25)" : "var(--card)"};
  cursor: pointer;
  text-align: left;
  transition: 0.2s ease;

  &:hover {
    border-color: rgba(117, 172, 56, 0.6);
  }
`;

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBox = styled.div<{ $active?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${(p) =>
    p.$active ? "var(--action-green)" : "var(--secondary)"};
  color: ${(p) => (p.$active ? "#fff" : "var(--trust-green)")};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SourceTitle = styled.div`
  font-weight: 700;
  color: var(--trust-green);
`;

const SourceDesc = styled.div`
  font-size: 12px;
  color: rgba(0, 77, 55, 0.55);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 700;
  color: var(--trust-green);
`;

const Req = styled.span`
  color: var(--action-orange);
  margin-left: 4px;
`;

const baseInput = `
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--trust-green);
  padding: 0 12px;
  outline: none;
  transition: 0.15s ease;

  &:focus {
    border-color: rgba(117, 172, 56, 0.9);
    box-shadow: 0 0 0 4px rgba(117, 172, 56, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--muted);
  }
`;

const InputWrap = styled.div`
  position: relative;
`;

const InputEl = styled.input`
  ${baseInput}
  padding-right: 12px;
`;

const Unit = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 77, 55, 0.45);
  background: rgba(239, 231, 218, 0.75);
  padding: 2px 6px;
  border-radius: 8px;
`;

const SelectWrap = styled.div`
  position: relative;
`;

const SelectEl = styled.select`
  ${baseInput}
  padding-right: 12px;
`;

const TextareaEl = styled.textarea`
  width: 100%;
  min-height: 92px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--trust-green);
  padding: 10px 12px;
  outline: none;
  resize: none;
  transition: 0.15s ease;

  &:focus {
    border-color: rgba(117, 172, 56, 0.9);
    box-shadow: 0 0 0 4px rgba(117, 172, 56, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--muted);
  }
`;

const Actions = styled.div`
  margin-top: 28px;
  display: flex;
  justify-content: center;

  .spin {
    animation: spin 0.9s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const PrimaryButton = styled.button`
  height: 44px;
  padding: 0 22px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  color: #fff;
  background: var(--action-orange);
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 0;
  color: rgba(0, 77, 55, 0.45);
`;

const CenterWrap = styled.div`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const SuccessIconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: var(--action-green);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const H2 = styled.h2`
  margin: 0 0 8px;
  color: var(--trust-green);
`;

const P = styled.p`
  margin: 0 0 18px;
  color: rgba(0, 77, 55, 0.6);
`;

const ErrorBanner = styled.div`
  max-width: 900px;
  margin: 0 auto 16px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(185, 28, 28, 0.25);
  background: rgba(185, 28, 28, 0.06);
  color: #7f1d1d;
  font-weight: 600;
  font-size: 13px;
`;

/* ---------- Partner dropdown (styled) ---------- */

const OrgDropdown = styled.div`
  position: relative;
`;

const OrgInput = styled.input`
  ${baseInput}
`;

const OrgMenu = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--card);
  overflow: hidden;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.08);
`;

const OrgItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: none;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: rgba(222, 245, 186, 0.25);
  }
`;

const OrgName = styled.div`
  font-weight: 700;
  color: var(--trust-green);
  font-size: 13px;
`;

const RoadmapCard = styled.div`
  margin-top: 16px;
  background: var(--card);
  border: 1px dashed rgba(0, 77, 55, 0.25);
  border-radius: 14px;
  padding: 18px;
`;

const RoadmapTitle = styled.div`
  font-weight: 800;
  color: var(--trust-green);
  margin-bottom: 6px;
`;

const RoadmapText = styled.div`
  color: rgba(0, 77, 55, 0.6);
  font-size: 13px;
  font-weight: 600;
`;

const OrgHintRow = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OrgHintText = styled.div`
  font-size: 12px;
  color: rgba(0, 77, 55, 0.55);
`;

const SelectedTag = styled.div`
  margin-top: 8px;
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  color: var(--trust-green);
  background: rgba(222, 245, 186, 0.35);
  border: 1px solid rgba(117, 172, 56, 0.25);
  padding: 4px 8px;
  border-radius: 999px;
`;

const SmallSpinner = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(0, 77, 55, 0.25);
  border-top-color: rgba(0, 77, 55, 0.7);
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const RoadmapIntroCard = styled.div`
  background: linear-gradient(
    135deg,
    rgba(222, 245, 186, 0.22),
    rgba(239, 231, 218, 0.55)
  );
  border: 1px solid rgba(117, 172, 56, 0.18);
  border-radius: 18px;
  padding: 18px 18px 16px;
  margin-bottom: 18px;
`;

const RoadmapTopGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 18px;

  @media (min-width: 900px) {
    grid-template-columns: 1.2fr 0.8fr;
  }
`;

const RoadmapMetaCard = styled.div`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 77, 55, 0.04);
`;

const SectionMiniTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: var(--trust-green);
  margin-bottom: 14px;
`;

const GridTwo = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const RoadmapHint = styled.div`
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(0, 77, 55, 0.58);
`;

const StaticInfoRow = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(222, 245, 186, 0.18);
  border: 1px solid rgba(117, 172, 56, 0.18);
`;

const StaticInfoLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 77, 55, 0.55);
  margin-bottom: 3px;
`;

const StaticInfoValue = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--trust-green);
`;

const RoadmapSectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const RoadmapSectionCard = styled.div`
  background: var(--card);
  border: 1px solid rgba(0, 77, 55, 0.1);
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 14px 32px rgba(0, 77, 55, 0.05);
`;

const RoadmapSectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const RoadmapSectionIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: rgba(222, 245, 186, 0.35);
  color: var(--trust-green);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RoadmapSectionTitle = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: var(--trust-green);
  margin-bottom: 3px;
`;

const RoadmapSectionSubtitle = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: rgba(0, 77, 55, 0.56);
  font-weight: 600;
`;

const SectionFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;
